import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { DateInvite } from '@/api/types';
import { ApiError, errorMessage } from '@/api/errors';
import { qk } from '@/api/queries';
import { useAuth } from '@/stores/auth';
import { toast } from '@/stores/toasts';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';
import { notifyApi, notifyKeys, useLate, type LateView } from '@/features/notify/api';
import { RescheduleSheet } from '@/features/invites/FriendsDates';
import { otherPerson } from '@/features/invites/share';
import { LATE_MINUTES, REASON_LABELS, canCancelNow, etaLabel, iCant, lateEligible, lateLine, myLate, partnerNotice } from './late';

function lateError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.details.reason === 'late_limit') return 'You’ve already let them know twice — they’ll wait for you.';
    if (e.details.reason === 'outside_window') return 'Running late opens 30 minutes before your date.';
    if (e.details.reason === 'not_scheduled') return 'This date doesn’t have a time yet.';
  }
  return errorMessage(e);
}

/** "Can't make it" — a kind preset reason, then (host) the reschedule sheet. */
function CantMakeItSheet({ inv, open, onClose, onReschedule }: { inv: DateInvite; open: boolean; onClose: () => void; onReschedule: () => void }) {
  const qc = useQueryClient();
  const late = useLate(inv.id, open);
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const name = otherPerson(inv)?.display_name ?? 'your date';
  const isHost = inv.role === 'host';
  useEffect(() => {
    if (open) {
      setReason(null);
      setErr(null);
    }
  }, [open]);
  const reasons = late.data?.reasons ?? Object.keys(REASON_LABELS).map((id) => ({ id, text: '' }));
  const send = async () => {
    if (!reason || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const v = await notifyApi.cantMakeIt(inv.id, reason);
      qc.setQueryData(notifyKeys.late(inv.id), v);
      onClose();
      if (isHost) onReschedule();
      else toast.success(`We told ${name}`, 'No hard feelings — they can offer new times.');
    } catch (e) {
      setErr(lateError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Can’t make it?" description={`It happens. Pick a reason and we’ll tell ${name} kindly${isHost ? ' — then choose new times' : ''}.`} testId="cant-make-it-sheet">
      <div role="radiogroup" aria-label="Reason" className="grid gap-2">
        {reasons.map((r) => (
          <button
            key={r.id}
            type="button"
            role="radio"
            aria-checked={reason === r.id}
            disabled={busy}
            onClick={() => setReason(r.id)}
            className={cn(
              'flex h-12 items-center justify-between rounded-full px-5 text-left text-[15px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg',
              reason === r.id ? 'bg-primary font-semibold text-ground' : 'bg-raised hover:bg-line',
            )}
            data-testid={`cant-reason-${r.id}`}
          >
            {REASON_LABELS[r.id] ?? r.text}
            {reason === r.id && <Icon name="check" size={18} />}
          </button>
        ))}
      </div>
      {err && (
        <p role="alert" className="mt-3 text-[15px] font-medium text-danger">
          {err}
        </p>
      )}
      <Button className="mt-5" block size="lg" icon={isHost ? 'calendar' : 'send'} disabled={!reason} loading={busy} onClick={() => send()} data-testid="cant-send">
        {isHost ? 'Tell them & pick new times' : `Let ${name} know`}
      </Button>
    </Sheet>
  );
}

/**
 * Running late and "Can't make it" (SPEC_V9 §D4), mounted in the lobby and on
 * the Home date card: the other person's notice as a banner, the 5/10/15 picker
 * inside T−30…T+30, and the reschedule deep link (`?action=reschedule`, lobby).
 */
export function RunningLate({ inv, variant = 'lobby' }: { inv: DateInvite; variant?: 'lobby' | 'card' }) {
  const me = useAuth((s) => s.me)!;
  const qc = useQueryClient();
  const eligible = lateEligible(inv);
  const late = useLate(inv.id, eligible);
  const [busy, setBusy] = useState<number | null>(null);
  const [cantOpen, setCantOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [params, setParams] = useSearchParams();
  const deepLinked = useRef(false);
  const isHost = inv.role === 'host';

  // One tap from a reminder: ?action=reschedule opens the host's reschedule
  // sheet, or the guest's "Can't make it" sheet.
  useEffect(() => {
    if (variant !== 'lobby' || deepLinked.current || params.get('action') !== 'reschedule') return;
    deepLinked.current = true;
    const next = new URLSearchParams(params);
    next.delete('action');
    setParams(next, { replace: true });
    if (!eligible && !(isHost && (inv.missed || inv.status === 'awaiting_host'))) return;
    if (isHost) setRescheduling(true);
    else setCantOpen(true);
  }, [variant, params, setParams, eligible, isHost, inv.missed, inv.status]);

  const view: LateView | undefined = late.data;
  const name = otherPerson(inv)?.display_name ?? 'Your date';
  const theirs = partnerNotice(view, me.id);
  const mine = myLate(view, me.id);
  const saidCant = iCant(view, me.id);
  const send = async (minutes: number) => {
    if (busy !== null) return;
    setBusy(minutes);
    try {
      const v = await notifyApi.runningLate(inv.id, minutes);
      qc.setQueryData(notifyKeys.late(inv.id), v);
      toast.success(`We told ${name} you’re ${minutes} min late`, 'Take a breath — the lobby stays open.');
    } catch (e) {
      toast.error('Couldn’t send that', lateError(e));
      void late.refetch();
    } finally {
      setBusy(null);
    }
  };

  const sheets = (
    <>
      <CantMakeItSheet inv={inv} open={cantOpen} onClose={() => setCantOpen(false)} onReschedule={() => setRescheduling(true)} />
      {isHost && (
        <RescheduleSheet
          invite={rescheduling ? inv : null}
          onClose={() => setRescheduling(false)}
          onDone={(v: DateInvite) => {
            qc.setQueryData(qk.dateInvite(v.id), v);
            void qc.invalidateQueries({ queryKey: notifyKeys.late(v.id) });
          }}
        />
      )}
    </>
  );
  if (!eligible) return sheets;

  const compact = variant === 'card';
  return (
    <div className={cn('text-left', compact ? 'mt-3 space-y-2' : 'mx-auto mt-5 max-w-sm space-y-3')} data-testid={`running-late-${variant}`}>
      {theirs && (
        <div className="flex items-start gap-3 rounded-card bg-yellow p-4 text-night" role="status" data-testid="late-banner" data-kind={theirs.kind}>
          <Icon name={theirs.kind === 'late' ? 'clock' : 'calendar'} size={20} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            {theirs.kind === 'late' ? (
              <>
                <p className="text-[17px] font-semibold leading-snug">{lateLine(name, theirs)}</p>
                {theirs.eta && <p className="text-[15px] tabular-nums opacity-75">Expected around {etaLabel(theirs.eta)}</p>}
              </>
            ) : (
              <>
                <p className="text-[17px] font-semibold leading-snug">{name} can’t make it</p>
                <p className="text-[15px] opacity-75">{view?.reasons.find((r) => r.id === theirs.reason)?.text ? `${capitalize(view!.reasons.find((r) => r.id === theirs.reason)!.text)}. ` : ''}No hard feelings — let’s find a new time.</p>
                {isHost && (
                  <Button className="mt-3 !bg-night !text-bone" size="sm" icon="calendar" onClick={() => setRescheduling(true)} data-testid="late-banner-reschedule">
                    Pick new times
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {view?.can_late && !saidCant && (
        <div className="rounded-card bg-surface p-4" data-testid="late-picker">
          <p className="text-[15px] font-semibold">{mine ? `You told ${name} you’re ${mine.minutes} min late` : 'Running late?'}</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {LATE_MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => void send(m)}
                disabled={busy !== null}
                aria-busy={busy === m || undefined}
                className="h-11 rounded-full bg-raised text-[15px] font-semibold tabular-nums transition-[background-color,transform] duration-150 ease-out hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg enabled:active:scale-[0.96] disabled:opacity-50"
                data-testid={`late-${m}`}
              >
                {busy === m ? '…' : `${m} min`}
              </button>
            ))}
          </div>
          {view.late_left === 1 && <p className="mt-2 text-[13px] text-muted">You can update them once more.</p>}
        </div>
      )}
      {canCancelNow(inv) && !saidCant && (
        <button
          type="button"
          onClick={() => setCantOpen(true)}
          className={cn('inline-flex h-11 items-center gap-1.5 text-[15px] font-medium text-muted underline-offset-4 hover:text-fg hover:underline', compact ? '' : 'mx-auto flex justify-center')}
          data-testid="cant-make-it"
        >
          <Icon name="calendar" size={16} /> Can’t make it?
        </button>
      )}
      {saidCant && (
        <p className="text-center text-[15px] text-muted" data-testid="cant-sent">
          You told {name} you can’t make it{isHost ? '' : ' — they’ll offer new times'}.
        </p>
      )}
      {sheets}
    </div>
  );
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
